import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@mui/material';
import { applicationApi } from 'app/services/api';
import {
  AutocompleteWithInfinityLoad,
  onAutocompleteChangeInForm,
} from 'app/shared/components/form/Autocomplete';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useForm from 'hooks/useForm';
import { buildURLQuery } from 'utils/urlQuery';

import { openFormPreviewAction } from 'store/forms/actions';
import { closeDialogAction } from 'store/global/actions';

import { formsTestIds } from '../forms.testIds';

const createFilterParam = filterText =>
  buildURLQuery({ loanNumber: filterText, fullAddress: filterText, oldLoanNumber: filterText });

const PrePreviewFormBasedOnLoanModal = ({
  formId,
  historyEntityId,
  formType,
  selectedPage,
  setIsLoading,
  isLoading,
}) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, setInForm, validateField } = useForm({
    loanId: null,
  });

  const loadOptions = useCallback(
    ({ filterText, pageNumber, pageSize }) =>
      applicationApi
        .getLoanInfo({
          pageNumber,
          pageSize,
          filter: filterText ? createFilterParam(filterText) : undefined,
        })
        .then(({ data: { data } }) => {
          return data.map(({ address, loanPurpose, loanNumber, loanId }) => {
            // TODO: use fullAddress from response once such endpoint is implemented
            const { street, city, state, zip } = address || {};
            const fullAddress = address ? `${street} ${city}, ${state} ${zip}` : 'N/A';

            return {
              name: [`${loanNumber} - ${fullAddress}`, loanPurpose].join(','),
              value: loanId,
            };
          });
        }),
    [],
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      dispatch(
        openFormPreviewAction({
          formId,
          historyEntityId,
          formType,
          selectedPage,
          loanId: form.loanId.value,
        }),
      );

      dispatch(closeDialogAction());
    },
    [dispatch, form, formId, historyEntityId, formType, selectedPage],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center p-24"
      data-ischanged={isFormChanged}
      onSubmit={onSubmit}
    >
      <AutocompleteWithInfinityLoad
        disableClearable
        className="mb-24"
        disabled={isLoading}
        label="Choose loan"
        name="loanId"
        setIsLoading={setIsLoading}
        value={form.loanId || ''}
        onChange={onAutocompleteChange}
        onLoadMore={loadOptions}
      />

      <Button
        className="whitespace-nowrap self-end"
        data-testid={formsTestIds.PRE_PREVIEW_LOAN_FORM_SUBMIT_BUTTON}
        type="submit"
        variant="contained"
      >
        Open
      </Button>
    </form>
  );
};

PrePreviewFormBasedOnLoanModal.propTypes = {
  ...WithLoadingPropTypes,
  formId: PropTypes.string.isRequired,
  formType: PropTypes.string.isRequired,
  selectedPage: PropTypes.string.isRequired,
  historyEntityId: PropTypes.string,
};

export default compose(withLoading, memo)(PrePreviewFormBasedOnLoanModal);
